import * as React from 'react';

import { PlanType } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';

import { UIButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { emailCountAtom } from '~teacher/components/Account/TeamMembers/InviteTeamMembersDialog';
import { useTeamSubscriptionPlan } from '~teacher/components/Account/TeamMembers/TeamMembersTable';
import { ContactSalesLinkButton } from '~teacher/components/Account/TeamMembers/TeamMembersTable/AddSeatsDialog/Components/ContactSalesLinkButton';

import platform from '../../../../../../../../../shared/Platform';
import { ManageSeatsButton } from './Components/ManageSeatsButton';
import { useCalculateSeatsAvailable } from './hooks';

const isDialogOpen = atom<boolean>(false);

export function OverPurchasedSeatsDialog() {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  const teamSubscriptionPlan = useTeamSubscriptionPlan(selectedTeamId);

  const [isOpen, setIsOpen] = useAtom(isDialogOpen);
  const emailCount = useAtomValue(emailCountAtom);
  const resetEmailCount = useResetAtom(emailCountAtom);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { seatsAvailable } = useCalculateSeatsAvailable(selectedTeamId);

  const handleClose = () => {
    setIsOpen(false);
    resetEmailCount();
    setIsSubmitting(false);
  };

  return (
    <Dialog
      titleWeight="medium"
      padding="confirmation"
      id="over-invited-add-seats-dialog"
      title={translate('Add Seats to Your Subscription')}
      open={!platform.isWrapperApp() && isOpen}
      onClose={handleClose}
      hasCustomCloseButton
    >
      <div className="flex flex-col gap-12 pt-8 ">
        <div className="flex flex-col gap-4 font-sans font-700 text-base leading-8">
          <p
            dangerouslySetInnerHTML={{
              __html: translate(
                'You sent {0} invites but only have {1} available seat(s).'
              ).format(
                `<span class="font-700">${emailCount}</span>`,
                `<span class="font-700">${seatsAvailable}</span>`
              ),
            }}
          />
          <p>
            {translate(
              'You can buy more seats for your invitees by managing your subscription.'
            )}
          </p>
        </div>
        <div className="flex flex-row-reverse gap-4">
          {teamSubscriptionPlan.data === PlanType.Organization ? (
            <ContactSalesLinkButton
              onClick={() => {
                setIsOpen(false);
              }}
            />
          ) : (
            <ManageSeatsButton
              isLoading={isSubmitting}
              onSubmit={() => {
                setIsOpen(false);
              }}
              onSubmitClick={() => {}}
              selectedTeamId={selectedTeamId}
            >
              {translate('Manage Subscription')}
            </ManageSeatsButton>
          )}
          <UIButton isDisabled={isSubmitting} onClick={handleClose}>
            {translate('Done')}
          </UIButton>
        </div>
      </div>
    </Dialog>
  );
}

OverPurchasedSeatsDialog.useOpenDialog = () => {
  const setIsDialogOpen = useSetAtom(isDialogOpen);
  return () => {
    setIsDialogOpen(true);
  };
};

export function getDidOverInvitePurchasedSeats({
  emailCount,
  seatsAvailable,
}: {
  emailCount: number;
  seatsAvailable: number;
}) {
  return emailCount > seatsAvailable;
}

export function useDidOverInvitePurchasedSeats(teamId: string) {
  const { seatsAvailable, queries } = useCalculateSeatsAvailable(teamId);

  const didOverInvitePurchasedSeats = (emailCount: number) =>
    getDidOverInvitePurchasedSeats({
      emailCount,
      seatsAvailable,
    });

  return {
    didOverInvitePurchasedSeats,
    ...queries,
  };
}
